import { Node } from "@tiptap/core";
import { ReactNodeViewRenderer } from "@tiptap/react";
import ContentBlockNodeView from "./ContentBlockNodeView";

/**
 * ContentBlockNode 扩展
 * 用于识别和渲染模板中的内容块，支持模板驱动的智能编辑器
 * V2.5: 支持instanceId，实现内容块实例管理
 */
export const ContentBlockNode = Node.create({
    name: "contentBlock",
    group: "block",
    atom: true, // 将节点作为一个整体处理
    draggable: true, // 启用拖拽功能
    inline: false,
    selectable: true,

    // 定义节点属性
    addAttributes() {
        return {
            // V2.5 新增：实例ID，用于唯一标识每个内容块实例
            instanceId: {
                default: null,
                parseHTML: (element) => element.getAttribute("data-instance-id"),
                renderHTML: (attributes) => {
                    if (!attributes.instanceId) {
                        return {};
                    }
                    return {
                        "data-instance-id": attributes.instanceId,
                    };
                },
            },
            // 内容块类型，用于关联到 SidePanel 中的对应组件
            contentBlockType: {
                default: null,
                parseHTML: (element) => element.getAttribute("data-content-block-type"),
                renderHTML: (attributes) => {
                    if (!attributes.contentBlockType) {
                        return {};
                    }
                    return {
                        "data-content-block-type": attributes.contentBlockType,
                    };
                },
            },
            // 内容块唯一ID（业务层面的ID，区别于instanceId）
            contentBlockId: {
                default: null,
                parseHTML: (element) => element.getAttribute("data-content-block-id"),
                renderHTML: (attributes) => {
                    if (!attributes.contentBlockId) {
                        return {};
                    }
                    return {
                        "data-content-block-id": attributes.contentBlockId,
                    };
                },
            },
            // 内容块显示标签
            contentBlockLabel: {
                default: "点击右侧插入内容",
                parseHTML: (element) => element.getAttribute("data-content-block-label"),
                renderHTML: (attributes) => {
                    if (!attributes.contentBlockLabel) {
                        return {};
                    }
                    return {
                        "data-content-block-label": attributes.contentBlockLabel,
                    };
                },
            },
            // 数据键，用于指定使用哪个数据项
            dataKey: {
                default: null,
                parseHTML: (element) => element.getAttribute("data-key"),
                renderHTML: (attributes) => {
                    if (!attributes.dataKey) {
                        return {};
                    }
                    return {
                        "data-key": attributes.dataKey,
                    };
                },
            },
            // 内容块状态：pending(待填充) | filled(已填充)
            status: {
                default: "pending",
            },
            // V2.5 新增：显示模式
            viewMode: {
                default: "summary",
                parseHTML: (element) => element.getAttribute("data-view-mode"),
                renderHTML: (attributes) => {
                    if (!attributes.viewMode) {
                        return {};
                    }
                    return {
                        "data-view-mode": attributes.viewMode,
                    };
                },
            },
            filterRevision: {
                default: null,
                parseHTML: (element) => element.getAttribute("data-filter-revision"),
                renderHTML: (attributes) => {
                    if (!attributes.filterRevision) {
                        return {};
                    }
                    return {
                        "data-filter-revision": attributes.filterRevision,
                    };
                },
            },
        };
    },

    // 解析HTML时如何识别此节点类型
    parseHTML() {
        return [
            {
                tag: "div[data-content-block-type]",
                getAttrs: (element) => {
                    const instanceId = element.getAttribute("data-instance-id");
                    const contentBlockType = element.getAttribute("data-content-block-type");
                    const contentBlockId = element.getAttribute("data-content-block-id");
                    const contentBlockLabel = element.getAttribute("data-content-block-label");
                    const dataKey = element.getAttribute("data-key");
                    const viewMode = element.getAttribute("data-view-mode");
                    const filterRevision = element.getAttribute("data-filter-revision");

                    return {
                        instanceId,
                        contentBlockType,
                        contentBlockId,
                        contentBlockLabel: contentBlockLabel || "点击右侧插入内容",
                        dataKey,
                        viewMode: viewMode || "summary",
                        filterRevision,
                        status: "pending",
                    };
                },
            },
        ];
    },

    // 渲染为HTML
    renderHTML({ HTMLAttributes, node }) {
        const { instanceId, contentBlockType, contentBlockId, contentBlockLabel, dataKey, status, viewMode, filterRevision } = node.attrs;

        const attrs = {
            "data-content-block-type": contentBlockType,
            "data-content-block-label": contentBlockLabel,
            "data-status": status,
            class: `contentBlock-node contentBlock-${status}`,
            ...HTMLAttributes,
        };

        if (instanceId) {
            attrs["data-instance-id"] = instanceId;
        }

        if (contentBlockId) {
            attrs["data-content-block-id"] = contentBlockId;
        }

        if (dataKey) {
            attrs["data-key"] = dataKey;
        }

        if (viewMode) {
            attrs["data-view-mode"] = viewMode;
        }

        if (filterRevision) {
            attrs["data-filter-revision"] = filterRevision;
        }

        return ["div", attrs, contentBlockLabel || "点击右侧插入内容"];
    },

    // 使用自定义React组件渲染节点
    addNodeView() {
        return ReactNodeViewRenderer(ContentBlockNodeView);
    },

    // 添加命令
    addCommands() {
        return {
            // 插入内容块
            insertContentBlock:
                (options = {}) =>
                ({ chain }) => {
                    const { instanceId, contentBlockType, contentBlockLabel } = options;

                    return chain()
                        .insertContent({
                            type: this.name,
                            attrs: {
                                instanceId,
                                contentBlockType,
                                contentBlockLabel: contentBlockLabel || "点击右侧插入内容",
                                status: "pending",
                            },
                        })
                        .run();
                },

            // V2.5 新增：更新内容块实例属性
            updateContentBlockInstance:
                (instanceId, attrs) =>
                ({ tr, state, dispatch }) => {
                    let updated = false;

                    state.doc.descendants((node, pos) => {
                        if (node.type.name === this.name && node.attrs.instanceId === instanceId && !updated) {
                            tr.setNodeMarkup(pos, null, {
                                ...node.attrs,
                                ...attrs,
                            });
                            updated = true;
                        }
                    });

                    if (updated && dispatch) {
                        dispatch(tr);
                        return true;
                    }

                    return false;
                },

            // 替换内容块为实际内容
            replaceContentBlock:
                (contentBlockType, content) =>
                ({ tr, state, dispatch }) => {
                    let replaced = false;

                    state.doc.descendants((node, pos) => {
                        if (node.type.name === this.name && node.attrs.contentBlockType === contentBlockType && node.attrs.status === "pending") {
                            // 找到第一个匹配的内容块并替换
                            if (!replaced) {
                                const from = pos;
                                const to = pos + node.nodeSize;

                                // 如果content是字符串，转换为HTML内容
                                let insertContent;
                                if (typeof content === "string") {
                                    insertContent = content;
                                } else {
                                    insertContent = content;
                                }

                                tr.replaceWith(from, to, state.schema.text(""));
                                tr.insertText(""); // 清空位置

                                // 在相同位置插入新内容
                                if (typeof insertContent === "string") {
                                    // 如果是HTML字符串，需要解析
                                    const tempDiv = document.createElement("div");
                                    tempDiv.innerHTML = insertContent;

                                    // 简单处理：如果包含HTML标签，插入为HTML，否则插入为文本
                                    if (insertContent.includes("<")) {
                                        tr.replaceWith(from, from, state.schema.nodes.paragraph.create({}, state.schema.text(tempDiv.textContent || insertContent)));
                                    } else {
                                        tr.insertText(insertContent, from);
                                    }
                                }

                                replaced = true;
                            }
                        }
                    });

                    if (replaced && dispatch) {
                        dispatch(tr);
                        return true;
                    }

                    return false;
                },
        };
    },
});

export default ContentBlockNode;
